import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, FAQPageSchema } from '@/components/base/SchemaOrg';
import { allComics, categories, features, faqs, testimonials } from '@/mocks/comics';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'hot' | 'new' | 'all'>('hot');

  const hotComics = allComics.filter((c) => c.isHot);
  const newComics = allComics.filter((c) => c.isNew);
  const displayComics = activeTab === 'hot' ? hotComics : activeTab === 'new' ? newComics : allComics;

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title="韩漫免费_韩国漫画全集大全_韩漫下拉式在线阅读-酷漫屋"
        description="酷漫屋提供海量免费韩国漫画全集下拉式在线阅读，资源丰富更新快，类型多样齐全，操作简单便捷，是韩漫爱好者的首选平台，让您随时随地畅享阅读乐趣。"
        url="https://www.kumanwu.com/"
      />
      <FAQPageSchema questions={faqs} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[620px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20comics%20collection%20display%2C%20colorful%20comic%20book%20covers%20arranged%20artistically%2C%20warm%20ambient%20lighting%2C%20clean%20modern%20library%20background%2C%20cozy%20reading%20atmosphere%2C%20soft%20bokeh%20effect%2C%20editorial%20style%2C%20rich%20warm%20tones%2C%20no%20text%20no%20logos&width=1800&height=900&seq=hero-bg-2026&orientation=landscape"
              alt="酷漫屋 - 韩国漫画在线阅读"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full max-w-3xl px-4 text-center">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  <strong>韩漫免费</strong>阅读，从这里开始
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                  酷漫屋汇聚海量<strong>韩国漫画</strong>资源，支持<strong>下拉式阅读</strong>，
                  免费、高清、更新快，随时随地畅享<strong>在线阅读</strong>乐趣
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/category/all"
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <Link
                    to="/about"
                    className="px-8 py-3 rounded-full bg-white/20 text-white font-semibold text-sm hover:bg-white/30 backdrop-blur-sm transition-all whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="w-full bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-wrap items-center justify-center gap-8 md:gap-16">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">2,000+</p>
                <p className="text-xs text-foreground-500 mt-1">漫画资源</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">50万+</p>
                <p className="text-xs text-foreground-500 mt-1">日活跃用户</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">99.9%</p>
                <p className="text-xs text-foreground-500 mt-1">可用率</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">10万+</p>
                <p className="text-xs text-foreground-500 mt-1">每日更新章节</p>
              </div>
            </div>
          </section>

          {/* Tabs: Hot / New / All Comics */}
          <section className="w-full py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  漫画列表
                </h2>
                <div className="flex items-center bg-background-100 rounded-full p-1">
                  {([
                    { key: 'hot' as const, label: '热门推荐' },
                    { key: 'new' as const, label: '最新上架' },
                    { key: 'all' as const, label: '全部漫画' },
                  ]).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                        activeTab === tab.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {displayComics.slice(0, 12).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-200 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-all whitespace-nowrap"
                >
                  查看全部漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Category Quick Links */}
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                分类浏览
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {categories.filter((c) => c !== '全部').map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${cat}`}
                    className="flex items-center justify-center px-4 py-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all group"
                  >
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                      {cat}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="w-full py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3">
                为什么选择酷漫屋？
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-12 max-w-lg mx-auto">
                我们用心打造最好的韩漫阅读平台，让每一位漫迷都能享受极致的阅读体验
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, i) => (
                  <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-100 transition-all group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-100 transition-colors">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3">
                用户好评
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-12">
                来自酷漫屋用户的真实反馈
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <div key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-sm text-accent-500"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.comment}</p>
                    <div className="flex items-center gap-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-12">
                关于酷漫屋，您可能想了解的问题
              </p>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="group rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="w-full py-16 bg-primary-500">
            <div className="max-w-2xl mx-auto px-4 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
                准备好了吗？立即开始您的韩漫之旅
              </h2>
              <p className="text-sm text-background-200 mb-8">
                数千部免费韩国漫画等您来发掘，随时随地，想读就读！
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-50 text-primary-600 font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap"
              >
                立即探索 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}